import type { Purchase } from './mock-data'

/**
 * 134 — goods carried over from another program (the owner, 2026-10-02:
 * „fina-დან შემოტანილი … ახალი შესყიდვა კი არა, არსებულია — უბრალოდ ჩვენს
 * აპლიკაციაში გადმოვიტანთ").
 *
 * One line of an opening-stock import, as `import_opening_stock` takes it:
 * the barcode finds the shop's product or the line creates it, and a given
 * `quantity` BECOMES the active branch's stock. `quantity: null` = the file
 * gave none: the stock is left as it is. The import itself books no purchase;
 * since 1.37 the owner may book ONE „საწყისი ნაშთი" purchase for it (below).
 */
export type OpeningItem = {
  barcode: string | null
  extra_barcodes?: string[]
  name: string
  category_id?: string | null
  purchase_price: number
  sale_price: number
  wholesale_price?: number | null
  unit?: string | null
  photo_url?: string | null
  plu_code?: string | null
  expiry_date?: string | null
  model_id?: string | null
  model_name?: string | null
  size?: string | null
  color?: string | null
  quantity: number | null
}

/**
 * 1.37 — the opening stock written into the books (the owner, 2026-10-12, for a
 * shop leaving Optimo: „ჩვენს პროექტს არ ეცოდინება რამდენი დახარჯა … საიდან
 * მოვიდა პროდუქცია?"). One bookkeeping purchase under this name holds what the
 * shop started with: no supplier, paid, the shelf left as the import set it —
 * the shape SAMGORI2026's fresh start got by hand on 2026-10-07. Profit is not
 * touched: a purchase is not a profit term (lib/profit.ts), each unit's cost
 * reaches it when the unit sells.
 */
export const OPENING_PURCHASE_NOTE = 'საწყისი ნაშთი'

/** One line of `import_opening_stock`'s answer — the product as written and its shelf at the branch. */
export type OpeningAnswer = {
  product: { id: string; name: string; barcode: string | null; purchase_price: number }
  branch_quantity: number
}

export type OpeningPurchaseLine = {
  product_id: string
  product_name: string
  barcode: string | null
  quantity: number
  unit_cost: number
}

/**
 * The purchase lines for an opening import: every line whose stock the file
 * gave and that left something on the shelf, at the cost the server wrote (the
 * one each sale will take as its cost). The answer comes back in the order the
 * lines were sent (WITH ORDINALITY), so index i answers sent line i.
 *
 * 158: the import folds a product's rows into one line; if two answers still
 * name one product, the shelf holds the LAST — one line, that one.
 */
export function openingPurchaseLines(
  sent: Pick<OpeningItem, 'quantity'>[],
  answered: OpeningAnswer[],
): OpeningPurchaseLine[] {
  const byProduct = new Map<string, OpeningPurchaseLine>()
  answered.forEach((a, i) => {
    if (sent[i]?.quantity == null) return
    if (!(a.branch_quantity > 0)) { byProduct.delete(a.product.id); return }
    byProduct.set(a.product.id, {
      product_id: a.product.id,
      product_name: a.product.name,
      barcode: a.product.barcode,
      quantity: a.branch_quantity,
      unit_cost: a.product.purchase_price,
    })
  })
  return [...byProduct.values()]
}

/**
 * The opening purchase this branch already has, if any — so a second upload of
 * the file books nothing (the stock it sets is already idempotent). Recognised
 * by its note (this import) or its supplier name (SAMGORI's, written by hand),
 * never on a supplier's purchase. A shop with no active branch has one shelf:
 * any opening purchase counts. With branches, a branch-less one (booked while
 * the shop had one) belongs to the default branch only — a branch opened later
 * books its own (Codex's review #3).
 */
export function findOpeningPurchase<P extends Pick<Purchase, 'supplier_id' | 'supplier_name' | 'note' | 'branch_id'>>(
  purchases: P[],
  branchId: string | null,
  defaultBranchId: string | null,
): P | null {
  return purchases.find((p) =>
    p.supplier_id == null
    && (p.supplier_name === OPENING_PURCHASE_NOTE || (p.note ?? '').startsWith(OPENING_PURCHASE_NOTE))
    && (branchId == null || p.branch_id === branchId || (p.branch_id == null && branchId === defaultBranchId)),
  ) ?? null
}

/**
 * The lines an existing opening purchase does not hold yet. Booking is finished
 * by appending exactly these, so a failed or partial booking completes on the
 * next upload of the file and a full one books nothing twice (Codex #1, #2);
 * a second file of further products joins the same opening purchase.
 */
export function missingOpeningLines(lines: OpeningPurchaseLine[], booked: Set<string>): OpeningPurchaseLine[] {
  return lines.filter((l) => !booked.has(l.product_id))
}

type CostRow = { key?: string | null; quantity: number; cost: number; knownCost?: number | null }

/**
 * The cost ONE product's rows come in at once the import folds them (1.40,
 * Codex's build review #2) — the write and the preview both read it here. A
 * row with no cost (empty or 0) counts as it would alone:
 * - a product the shop has (`knownCost` a number): at its current cost — what
 *   a lone blank row is restocked at, and what the opening import keeps. It
 *   used to blend as ₾0: cost ₾8, 5 blank + 5 at ₾10 booked ₾50, not ₾90;
 * - a new product (`knownCost` null): left out of the blend, it has no cost to
 *   stand for; all blank → 0, as one blank row is.
 * Weighted by the units each row brings, to the tetri (one cost on every row
 * is that cost, as given). With no units at all — rows that only carry a cost
 * — there is nothing to weigh: the last cost the file gave, else the known one.
 */
export function foldCost(rows: readonly { qty: number; cost: number }[], knownCost: number | null): number {
  let units = 0
  let value = 0
  let last: number | null = null
  let same: number | null | undefined          // undefined: no row yet; null: two costs
  for (const r of rows) {
    if (r.cost > 0) last = r.cost
    const cost = r.cost > 0 ? r.cost : knownCost
    if (cost === null || !(r.qty > 0)) continue
    units += r.qty
    value += r.qty * cost
    same = same === undefined || same === cost ? cost : null
  }
  if (units > 0) return same != null ? same : Math.round((value / units) * 100) / 100
  return last ?? knownCost ?? 0
}

/**
 * The money an import books, as its preview names it — the opening value and
 * the receive mode's expense alike: each product's units (its rows folded, as
 * the import folds them) at `foldCost`, a blank cost at the shop's known cost
 * — what the server keeps and books, so the screen and the booked purchase
 * agree (Codex #6). A row with no `key` is a product of its own.
 */
export function openingValue(rows: CostRow[]): number {
  const all: { units: number; rows: { qty: number; cost: number }[]; known: number | null }[] = []
  const byKey = new Map<string, (typeof all)[number]>()
  for (const r of rows) {
    const one = { qty: r.quantity, cost: r.cost }
    const g = r.key ? byKey.get(r.key) : undefined
    if (g) { g.units += r.quantity; g.rows.push(one); continue }
    const fresh = { units: r.quantity, rows: [one], known: r.knownCost ?? null }
    all.push(fresh)
    if (r.key) byKey.set(r.key, fresh)
  }
  const sum = all.reduce((s, g) => (g.units > 0 ? s + g.units * foldCost(g.rows, g.known) : s), 0)
  return Math.round(sum * 100) / 100
}

/**
 * How many stocked products would come in at no cost: their units would sell
 * with a ₾0 cost and the profit would read too high. A cost the shop already
 * knows is kept by the server (`import_opening_stock` never overwrites it with
 * 0). Rows naming one barcode are one product, as the import folds them: it is
 * costless only if none of its rows gives a cost.
 */
export function costlessCount(rows: CostRow[]): number {
  const byKey = new Map<string, { quantity: number; costed: boolean }>()
  let n = 0
  for (const r of rows) {
    const costed = r.cost > 0 || (r.knownCost ?? 0) > 0
    if (!r.key) { if (r.quantity > 0 && !costed) n++; continue }
    const prev = byKey.get(r.key)
    if (prev) { prev.quantity += r.quantity; prev.costed = prev.costed || costed }
    else byKey.set(r.key, { quantity: r.quantity, costed })
  }
  for (const v of byKey.values()) if (v.quantity > 0 && !v.costed) n++
  return n
}

/** Read the guarded import's sale count from a Supabase error message. */
export function openingAfterSalesCount(message: unknown): number | null {
  const match = String(message ?? '').match(/OPENING_AFTER_SALES:(\d+)(?![\w.])/)
  if (!match) return null
  const count = Number(match[1])
  return Number.isSafeInteger(count) ? count : null
}

/**
 * A cell the file actually filled. An empty „რაოდენობა" is NOT a zero: in
 * an opening-stock import a zero empties the branch's shelf, an empty cell
 * leaves it alone (our own export writes that column empty on purpose).
 */
export function cellGiven(v: unknown): boolean {
  if (v == null) return false
  if (typeof v === 'number') return Number.isFinite(v)
  return String(v).trim() !== ''
}

/** A number from a cell the file filled; null when it filled none or wrote no number. */
export function cellNumber(v: unknown): number | null {
  if (!cellGiven(v)) return null
  const n = typeof v === 'number' ? v : parseFloat(decimalText(String(v)))
  return Number.isFinite(n) ? n : null
}

/** „2,5" → 2.5; with both marks the last is the decimal: „1,250.50" and „1.250,50" are 1250.5.
 *  One comma alone is always a decimal, as in every number field: „1,250" is 1.25. */
function decimalText(s: string): string {
  const comma = s.lastIndexOf(','), dot = s.lastIndexOf('.')
  if (comma === -1) return s
  if (dot === -1) return s.replace(',', '.')
  return comma > dot ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '')
}

/** 1.75 — a number cell of the import window: empty or a word is 0. A bare `parseFloat`
 *  read a text „12,5" as 12 and „9,75" as 9, with nothing on screen to say so. */
export const cellAmount = (v: unknown): number => cellNumber(v) ?? 0
